import React from 'react';
import axios from 'axios';

import {
    ComposedChart,
    BarChart,
    Bar,
    Cell,
    Line,
    XAxis,
    YAxis,
    Label,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from "recharts";

const COLORS = {
    'red' : {
        'on' : '#CC1F3B',
        'off' : '#EDADB8',
    },
    'blue' : {
        'on' : '#1344B2',
        'off' : '#B9C9EE',
    },
    'gray' : {
        'on' : '#999999',
        'off' : '#CCCCCC',
    },
}

/* Converts dates to integers for given keys starting from cumulative values exceeding given threshold, also for given day threshold. */
function dateDataToDayData(data=[], keyList=[], threshold=0, dayThreshold=1) {
    /* Initialize return value. */
    let dayData = []
    /* Initialize counter. */
    let totals = {}
    keyList.forEach((key) => {
        totals[key] = { total: 0, index: 0 }
    })
    /* Determine threshold indices. */
    data.forEach((entry, index) => {
        keyList.forEach((key) => {
            if (totals[key].total <= threshold) {
                totals[key] = {
                    total: totals[key].total + entry[key],
                    index: index,
                }
            }
            else {
                let dayValue = index - totals[key].index
                if (dayData.length <= dayValue) {
                    let dayEntry = {
                        date: dayValue,
                        origDates: {
                            [key]: entry.date
                        }
                    }
                    dayEntry[key] = entry[key]
                    dayData.push(dayEntry)
                }
                else {
                    dayData[dayValue-1][key] = entry[key]
                    dayData[dayValue-1].origDates[key] = entry.date
                }
            }
        })
    })
    /* Adjust data based on threshold indices. */
    return dayData.filter((entry) => (entry.date >= dayThreshold))
}

export default class Chart extends React.Component {
    constructor(props) {
        super(props)
        /* Define US states. */
        const states = [
            "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
            "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
            "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
            "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
            "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
            "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
            "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
        ]
        /* Define countries. */
        const countries = [
            'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
            'Armenia', 'Aruba', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh',
            'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
            'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burma', 'Burundi', 'Cabo Verde',
            'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Cayman Islands', 'Central African Republic', 'Chad',
            'Channel Islands', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Brazzaville)', 'Congo (Kinshasa)',
            'Costa Rica', "Cote d'Ivoire", 'Croatia', 'Cuba', 'Curacao', 'Cyprus', 'Czechia', 'Denmark',
            'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador', 'Egypt', 'El Salvador',
            'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Faroe Islands', 'Fiji', 'Finland',
            'France', 'French Guiana', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Gibraltar', 'Greece',
            'Greenland', 'Grenada', 'Guadeloupe', 'Guam', 'Guatemala', 'Guernsey', 'Guinea', 'Guinea-Bissau',
            'Guyana', 'Haiti', 'Holy See', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia',
            'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jersey', 'Jordan',
            'Kazakhstan', 'Kenya', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Liberia', 'Libya',
            'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macau', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
            'Mali', 'Malta', 'Martinique', 'Mauritania', 'Mauritius', 'Mayotte', 'Mexico', 'Moldova', 'Monaco',
            'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Namibia', 'Nepal', 'Netherlands', 'New Zealand',
            'Nicaragua', 'Niger', 'Nigeria', 'North Ireland', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
            'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
            'Puerto Rico', 'Qatar', 'Reunion', 'Romania', 'Russia', 'Rwanda', 'Saint Barthelemy',
            'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Martin', 'Saint Vincent and the Grenadines', 'San Marino',
            'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
            'Slovakia', 'Slovenia', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka',
            'St. Martin', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
            'Thailand', 'Timor-Leste', 'Togo', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Uganda', 'Ukraine',
            'United Arab Emirates', 'United Kingdom', 'Uruguay', 'Uzbekistan', 'Vatican City', 'Venezuela',
            'Vietnam', 'West Bank and Gaza', 'Western Sahara', 'Yemen', 'Zambia', 'Zimbabwe'
        ]
        /* Define data types. */
        const types = ['confirmed', 'deaths']
        /* Define color values. */
        const colors = ['red', 'blue', 'gray']
        /* Read params. */
        const params = new URLSearchParams(window.location.search);
        /* Decide data type. */
        const pDataType = (params.get('type') || '').toLowerCase()
        const dataType = (
            types.includes(pDataType)
            ? pDataType
            : 'confirmed'
        )
        /* Decide data key. */
        const pDataKey = this.toTitleCase((params.get('state') || ''))
        const dataKey = (
            states.includes(pDataKey)
            ? pDataKey
            : 'US'
        )
        /* Decide data level. */
        const pDataLevel = (dataKey !== 'US') ? ('state') : ('country')
        /* Decide comparision key. */
        const pCompDataKey = this.toTitleCase((params.get('compare') || ''))
        const compDataKey = (
            (
                pDataLevel === 'state'
                ? states.includes(pCompDataKey)
                : countries.includes(pCompDataKey)
            )
            ? pCompDataKey
            : ''
        )
        /* Decide final data level */
        const dataLevel = (compDataKey !== '' && pDataLevel === 'country') ? ('global') : (pDataLevel)
        /* Decide color scheme. */
        const pColorType = (params.get('color') || '').toLowerCase()
        const colorType = (
            colors.includes(pColorType)
            ? pColorType
            : 'gray'
        )
        this.compKeyList = (dataLevel === 'state') ? (states) : (countries)
        /* Create state. */
        this.state = {
            activeIndex: -1,
            data: null,
            chartData: null,
            dataType: dataType,
            dataKey: dataKey,
            compDataKey: compDataKey,
            dataLevel: dataLevel,
            colorType: colorType,
        }
    }

    componentDidMount() {
        /* Fetch state. */
        const { dataType, dataKey, compDataKey, dataLevel } = this.state
        /* Determine chart type */
        const isCompare = (compDataKey !== "")
        /* Make request for data. */
        axios.get(`https://covid.psg.nexstardigital.net/covid_ts_${dataLevel}_${dataType}_df.json`)
        .then((response) => {
            /* Fetch raw data. */
            const rawData = dataLevel === 'country' ? { 'US': response.data } : response.data
            /* Reformat into desired format. */
            const formettedData = Object.keys(rawData[dataKey]).map((key, index) => (
                {
                    date: key,
                    ...(Object.keys(rawData).reduce((acc, name) => (
                        {
                            ...acc,
                            [name]: rawData[name][key]
                        }
                    ), {}))
                }
            ))
            /* Parse data for charts. */
            const data = Array.from(
                formettedData
            /* Remove data before mininum date. */
            ).filter((item) => (
                isCompare || (this.getDateFromString(item.date) > this.getDateFromString('02-24-2020'))
            /* Sort data. */
            )).sort((firstItem, secondItem) => (
                this.getDateFromString(firstItem.date) - this.getDateFromString(secondItem.date)
            ))
            /* Update state. */
            this.setState({
                data: data,
                chartData: (isCompare ? dateDataToDayData(data, [dataKey, compDataKey], 25, 5) : data)
            })
        })
    }

    getDateFromString(dateString) {
        /* Converts given date in DD-MM-YYYY format to date object. */
        const [year, month, day] = dateString.split('-')
        const date = new Date(year, parseInt(month)-1, day)
        return date
    }

    toTitleCase(string) {
        /* Converts given string to title case. */
        return string.replace(
            /\w\S*/g,
            (txt) => (txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
        );
    }

    dateToText(dateString) {
        /* Decides on suffix for date. */
        const date = this.getDateFromString(dateString)
        const month = date.toLocaleString('default', { month: 'short' })
        const day = date.getDate()
        /* Date starting with 1 means suffix is th. */
        if (Math.floor(day / 10) === 1) {
            return `${month} ${day}th`
        }
        /* First. */
        else if ((day % 10) === 1) {
            return `${month} ${day}st`
        }
        /* Second. */
        else if ((day % 10) === 2) {
            return `${month} ${day}nd`
        }
        /* Third. */
        else if ((day % 10) === 3) {
            return `${month} ${day}rd`
        }
        /* All else. */
        else {
            return `${month} ${day}th`
        }
    }

    render() {
        /* Fetch state. */
        const { colorType, dataType, dataKey, dataLevel, compDataKey, data, chartData } = this.state
        /* Determine chart type */
        const isCompare = (compDataKey !== "")
        /* Decide color scheme. */
        const colorScheme = COLORS[colorType]
        /* Decide chart component. */
        const ChartComponent = (isCompare ? ComposedChart : BarChart)
        /* Define mouse events for chart. */
        const mouseEvents = {
            onMouseEnter: ((event) => {
                /* Check if tooltip is active */
                const isActive = event.isTooltipActive
                if (isActive) {
                    /* Register enter to analytics */
                    this.props.analytics.enter(
                        `${dataType}:${dataLevel}`,
                        isCompare ? `${dataKey}:${compDataKey}` : `${dataKey}`
                    )
                }
            }),
            onMouseMove: ((event) => {
                const index = event.activeTooltipIndex
                /* Check if tooltip is active */
                const isActive = event.isTooltipActive
                if (isActive) {
                    /* Update active index */
                    this.setState({
                      activeIndex: index,
                    })
                }
                else {
                    /* Remove active index */
                    this.setState({
                      activeIndex: -1,
                    })
                }
                /* Register move to analytics */
                this.props.analytics.move(
                    `${dataType}:${dataLevel}`,
                    isCompare ? `${dataKey}:${compDataKey}` : `${dataKey}`
                )
            }),
            onMouseLeave: ((event) => {
                /* Remove active index */
                this.setState({
                  activeIndex: -1,
                })
                /* Register exit to analytics */
                this.props.analytics.exit()
            }),
        }
        /* Detailed text for types. */
        const getText = (dataType) => {
            if (dataType === 'confirmed') {
                return ['confirmed', 'cases']
            }
            else if (dataType === 'deaths') {
                return ['reported', 'deaths']
            }
            else {
                return null
            }
        }
        /* Custom tooltip for better formatting. */
        const TooltipContent = ({ active, payload, label }) => {
            if (active) {
                const primaryPayload = (
                    payload.length >= 1 && payload[0].dataKey === dataKey
                    ? payload[0].payload
                    : null
                )
                const secondaryPayload = (
                    payload.length === 2 && payload[1].dataKey === compDataKey
                    ? payload[1].payload
                    : (payload.length === 1 && payload[0].dataKey === compDataKey
                        ? payload[0].payload
                        : null
                    )
                )
                return (
                    <div className="border rounded bg-white px-2 pt-2">
                        { (primaryPayload !== null) &&
                        <p className="text-left">
                            { isCompare &&
                            <span className="block-center box mr-1" style={{ backgroundColor: colorScheme.off }} />
                            }
                            <span className="font-weight-bold">
                                {`${
                                    isCompare
                                    ? dataKey + ` (${this.dateToText(primaryPayload.origDates[dataKey])})`
                                    : this.dateToText(label)
                                }`}
                            </span>
                            <br/>
                            {`${primaryPayload[dataKey] !== 0 ? primaryPayload[dataKey].toLocaleString() : 'No'} new`}
                            <br/>
                            {`${getText(dataType).join(' ')}`}
                        </p>
                        }
                        { (isCompare && secondaryPayload !== null) && 
                        <p className="text-left">
                            { isCompare &&
                            <span className="block-center line mr-1" style={{ backgroundColor: colorScheme.on }} />
                            }
                            <span className="font-weight-bold">
                                {`${
                                    isCompare
                                    ? compDataKey + ` (${this.dateToText(secondaryPayload.origDates[compDataKey])})`
                                    : this.dateToText(label)
                                }`}
                            </span>
                            <br/>
                            {`${secondaryPayload[compDataKey] !== 0 ? secondaryPayload[compDataKey].toLocaleString() : 'No'} new`}
                            <br/>
                            {`${getText(dataType).join(' ')}`}
                        </p>
                        }
                    </div>
                );
            }
            return null;
        };
        /* Render. */
        return (
            <React.Fragment>
                <div className="pb-3">
                    <h4 className="text-left text-capitalize">
                        {`${dataKey !== '' ? dataKey : 'US'} ${getText(dataType)[0]} Coronavirus ${getText(dataType)[1]} by Day`}
                    </h4>
                    { (isCompare) &&
                    <form className="form-inline">
                        <label htmlFor="compare-with-select">Compare with</label>
                        <select 
                            className="font-weight-bold"
                            name="compare-with"
                            id="compare-with-select"
                            defaultValue={compDataKey}
                            onChange={(event) => {
                                this.setState({
                                    compDataKey: event.target.value,
                                    chartData: dateDataToDayData(data, [dataKey, event.target.value], 25, 5)
                                })
                            }}
                        >
                            {
                                this.compKeyList.map((item) => (
                                    <option key={item} value={item}>{item}</option>
                                ))
                            }
                        </select>
                    </form>
                    }
                </div>
                { (chartData !== null) &&
                <ResponsiveContainer className="py-2" minWidth={200} minHeight={100} width="100%" height={400}>
                    <ChartComponent
                        data={chartData}
                        {...mouseEvents}
                    >
                        <XAxis dataKey="date" stroke="#cccccc" axisLine={false} tickLine={false} tick={false}>
                            <Label 
                                className="text-left font-weight-bold"
                                value={
                                    isCompare
                                    ? chartData[0].date
                                    : this.dateToText(chartData[0].date)
                                }
                                position="insideBottomLeft"
                            />
                            { isCompare &&
                            <Label 
                                className="text-center font-weight-bold"
                                value={`Days since 25 ${getText(dataType).join(' ')}`}
                                position="insideBottom"
                            />
                            }
                            <Label 
                                className="text-left font-weight-bold"
                                value={
                                    isCompare
                                    ? chartData[chartData.length-1].date
                                    : this.dateToText(chartData[chartData.length-1].date)
                                }
                                position="insideBottomRight"
                            />
                        </XAxis>
                        <YAxis axisLine={false} tickFormatter={(tick) => (tick.toLocaleString())} />
                        <Tooltip isAnimationActive={false} cursor={false} content={<TooltipContent />} />
                        <CartesianGrid vertical={false} stroke="#cccccc" />
                        <Bar
                            dataKey={dataKey}
                            fill="#cccccc"
                        >
                            {chartData.map((entry, index) => (
                                <Cell fill={index === this.state.activeIndex ? colorScheme.on : colorScheme.off} key={`cell-${index}`} />
                            ))}
                        </Bar>
                        { (isCompare) &&
                        <Line
                            dataKey={compDataKey}
                            stroke={colorScheme.on}
                            strokeWidth="2"
                            dot={false}
                            activeDot={{ stroke: colorScheme.off, strokeWidth: 2 }}
                            type="linear"
                        />
                        }
                    </ChartComponent>
                </ResponsiveContainer>
                }
                <p className="text-left pt-3">
                    Sources: The Center for Systems Science and Engineering at <a
                        target="_blank" rel="noopener noreferrer" href="https://coronavirus.jhu.edu/map.html"
                    >
                        Johns Hopkins University
                    </a>
                </p>
            </React.Fragment>
        )
    }
}